import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
}

export default function StockTakeCreatePage() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [isInitial, setIsInitial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch('/api/v1/warehouses')
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load warehouses');
        const data = await res.json();
        setWarehouses(data.items ?? data ?? []);
      })
      .catch(() => setError('Failed to load warehouses'))
      .finally(() => setLoading(false));
  }, []);

  async function startStockTake(clearExistingStock: boolean) {
    if (!warehouseId) {
      setError('Select a warehouse to start counting.');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/stock-takes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warehouseId, isInitial, clearExistingStock }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorBody(data));
      }
      const st = await res.json();
      setConfirmOpen(false);
      navigate(`/inventory/stock-takes/${st.id}/count`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start stock take');
    } finally {
      setCreating(false);
    }
  }

  function onStartClick() {
    if (!warehouseId) {
      setError('Select a warehouse to start counting.');
      return;
    }
    if (isInitial) {
      setConfirmOpen(true);
      return;
    }
    void startStockTake(false);
  }

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto w-full max-w-lg space-y-6">
        <Button variant="ghost" asChild>
          <Link to="/inventory/stock-takes">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to stock takes
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>New stock take</CardTitle>
            <CardDescription>
              Choose the warehouse you are counting. Product lines come from that location&apos;s
              assigned assortment — add products on the hub or spoke first.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="warehouse">Warehouse *</Label>
              <Select value={warehouseId || undefined} onValueChange={setWarehouseId}>
                <SelectTrigger id="warehouse" className="w-full">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {warehouseOptionLabel(w)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-3 rounded-md border p-3">
              <Checkbox
                id="is-initial"
                checked={isInitial}
                onCheckedChange={(checked) => setIsInitial(checked === true)}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="is-initial" className="cursor-pointer">
                  Initial stock take (opening stock)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Opening count from zero. Assign products first. This clears current quantities at
                  this location, then you count with batch number and expiry required. You can
                  submit a partial count and finish later.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" variant="outline" asChild>
                <Link to="/inventory/stock-takes">Cancel</Link>
              </Button>
              <Button type="button" onClick={onStartClick} disabled={creating || !warehouseId}>
                {creating ? 'Starting…' : 'Start counting'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={confirmOpen} onOpenChange={(open) => !creating && setConfirmOpen(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start opening stock take?</DialogTitle>
              <DialogDescription>
                This sets every quantity at this location to zero, then starts a blank count.
                Approve later will book what you count as opening stock. Open pick lists will block
                this. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={creating}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={creating}
                onClick={() => void startStockTake(true)}
              >
                {creating ? 'Starting…' : 'Clear stock and start'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
